import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Slider } from "@/components/ui/slider";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";

// Import LLM providers configuration
import llmProvidersConfig from "@/config/llm-providers.json";
import {
  isRealtimeModel,
  getRealtimeModelMessage,
  OPENAI_REALTIME_VOICES,
  DEFAULT_OPENAI_REALTIME_VOICE,
  isGeminiRealtimeModel,
  GEMINI_REALTIME_VOICES,
  GEMINI_REALTIME_LANGUAGES,
  DEFAULT_GEMINI_REALTIME_VOICE,
  DEFAULT_GEMINI_REALTIME_LANGUAGE,
} from "@/utils/realtimeModels";

// Dynamic model providers from configuration
const ALL_MODEL_PROVIDERS = Object.values(llmProvidersConfig.providers).map(provider => ({
  id: provider.id,
  name: provider.name
}));

// Providers offered in the dropdown. The commented-out ones are hidden for now:
// voxi-2-0 (src/llmProviders.js) cannot reach most of them, and an agent saved
// on one falls back to OpenAI. Uncomment an id to offer it again.
const ENABLED_PROVIDER_IDS = [
  "openai",
  "anthropic",
  "groq",
  "google",
  "bedrock-mantle",
  "sarvam",
  "openai-realtime",
  "google-realtime",
  "custom-realtime",
  // "mercury",
  // "ultravox",
  // "aws",
  // "openrouter",
  // "azure",
  // "mistralai",
  // "ollama",
  // "perplexity",
  // "together",
  // "xai",
  // "custom",
  // "livekit-openai",
  // "baseten",
  // "cerebras",
  // "deepseek",
  // "fireworks",
  // "telnyx",
];

const MODEL_PROVIDERS = ALL_MODEL_PROVIDERS.filter(provider =>
  ENABLED_PROVIDER_IDS.includes(provider.id)
);

// An assistant already saved on a hidden provider still shows it, rather than
// an empty select.
const providerOptions = (current?: string) => {
  if (!current || MODEL_PROVIDERS.some(p => p.id === current)) return MODEL_PROVIDERS;
  const saved = ALL_MODEL_PROVIDERS.find(p => p.id === current);
  return [...MODEL_PROVIDERS, { id: current, name: saved?.name ?? current }];
};

// Helper function to get all models for a provider with category information
const getModelsForProvider = (providerId: string) => {
  const provider = llmProvidersConfig.providers[providerId];
  if (!provider || !provider.models) return [];
  
  const allModels: Array<{ id: string; name: string; context?: number; category?: string; inputPrice?: number; outputPrice?: number }> = [];
  
  // Flatten all model categories into a single array with category info
  Object.entries(provider.models).forEach(([categoryName, modelCategory]) => {
    if (Array.isArray(modelCategory)) {
      modelCategory.forEach(model => {
        allModels.push({
          ...model,
          category: categoryName
        });
      });
    }
  });
  
  return allModels;
};

// Helper function to group models by category for better UI organization
const getGroupedModelsForProvider = (providerId: string) => {
  const provider = llmProvidersConfig.providers[providerId];
  if (!provider || !provider.models) return {};
  
  return provider.models;
};

// Bedrock's model list is live: voxi-2-0 proxies GET /v1/models (bearer token
// stays server-side). Same v2 base the outbound-call service uses.
const BEDROCK_PROVIDER_ID = "bedrock-mantle";
const getV2ModelsUrl = () => {
  const base = (
    import.meta.env.VITE_V2_CALL_SERVICE_URL ||
    import.meta.env.VITE_V1_CALL_SERVICE_URL ||
    ""
  )
    .replace(/\/+$/, "")
    .replace(/\/api\/calls$/, "");
  return `${base}/api/models?provider=${BEDROCK_PROVIDER_ID}`;
};

type ModelOption = { id: string; name: string; context?: number; category?: string; inputPrice?: number; outputPrice?: number };

export default function ModelTab() {
  const { control, setValue, watch, getValues } = useFormContext();
  const llm_provider = watch("llm_provider");
  const llm_model = watch("llm_model");

  const [bedrockModels, setBedrockModels] = useState<ModelOption[]>([]);
  const [bedrockStatus, setBedrockStatus] = useState<"idle" | "loading" | "error">("idle");
  const initialProvider = useRef(llm_provider);

  useEffect(() => {
    if (llm_provider !== BEDROCK_PROVIDER_ID || bedrockModels.length > 0) return;
    let cancelled = false;
    setBedrockStatus("loading");
    fetch(getV2ModelsUrl())
      .then(async (r) => {
        if (!r.ok) throw new Error(`models request failed (${r.status})`);
        return r.json();
      })
      .then((body) => {
        if (cancelled) return;
        const list: ModelOption[] = (body?.models ?? []).map((m: ModelOption) => ({ id: m.id, name: m.name || m.id }));
        setBedrockModels(list);
        setBedrockStatus("idle");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load Bedrock models", err);
        setBedrockStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [llm_provider, bedrockModels.length]);

  // Once the live list arrives, replace a model that belongs to the previous
  // provider. An assistant opened already on Bedrock keeps its saved model.
  useEffect(() => {
    if (llm_provider !== BEDROCK_PROVIDER_ID || bedrockModels.length === 0) return;
    if (bedrockModels.some((m) => m.id === llm_model)) return;
    if (llm_model && llm_provider === initialProvider.current) return;
    setValue("llm_model", bedrockModels[0].id, { shouldValidate: true });
  }, [llm_provider, bedrockModels, llm_model, setValue]);

  const modelsFor = (providerId: string): ModelOption[] =>
    providerId === BEDROCK_PROVIDER_ID ? bedrockModels : getModelsForProvider(providerId);

  // Get default model for a provider
  const getDefaultModelForProvider = (providerId: string) => {
    const models = modelsFor(providerId);
    if (models.length === 0) return "";
    
    // Skip looking for isDefault since model type doesn't have it
    // const defaultModel = models.find(model => model.isDefault);
    // if (defaultModel) return defaultModel.id;
    
    // Provider-specific defaults
    if (providerId === "openai") {
      const gpt4o = models.find(model => model.id === "gpt-4o");
      if (gpt4o) return gpt4o.id;
    } else if (providerId === "anthropic") {
      const claude35 = models.find(model => model.id === "claude-3-5-sonnet-latest");
      if (claude35) return claude35.id;
    } else if (providerId === "groq") {
      const gptOss = models.find(model => model.id === "openai/gpt-oss-20b");
      if (gptOss) return gptOss.id;
      const llama = models.find(model => model.id === "llama-3.3-70b-versatile");
      if (llama) return llama.id;
    } else if (providerId === "google") {
      const gemini = models.find(model => model.id === "gemini-2.5-flash");
      if (gemini) return gemini.id;
    } else if (providerId === "openrouter") {
      const openai = models.find(model => model.id === "openai/gpt-4o");
      if (openai) return openai.id;
    } else if (providerId === "ultravox") {
      const ultravoxDefault = models.find(model => model.id === "fixie-ai/ultravox");
      if (ultravoxDefault) return ultravoxDefault.id;
    } else if (providerId === "google-realtime") {
      const geminiLive = models.find(model => model.id === "gemini-3.8-live");
      if (geminiLive) return geminiLive.id;
    } else if (providerId === "mercury") {
      const mercury25 = models.find(model => model.id === "mercury-2.5");
      if (mercury25) return mercury25.id;
    }
    
    // Fall back to the first model
    return models[0]?.id || "";
  };

  // Only set default values for new assistants, not when editing existing ones
  useEffect(() => {
    // Check if we're editing an existing assistant by looking at URL params
    const urlParams = new URLSearchParams(window.location.search);
    const assistantId = urlParams.get('assistantId') || window.location.pathname.split('/').pop();
    const isEditingExisting = assistantId && assistantId !== 'new';
    
    // For existing assistants, don't interfere with form values set by AssistantDashboard
    if (isEditingExisting) {
      return;
    }
    
    // For new assistants, set defaults only if values are missing
    if (!llm_provider) {
      setValue("llm_provider", "openai", { shouldValidate: false });
    }
    if (!llm_model) {
      const defaultModel = getDefaultModelForProvider(llm_provider || "openai");
      setValue("llm_model", defaultModel, { shouldValidate: false });
    }
  }, []); // Empty dependency array - only run on mount

  const handleLlmModelChange = (version: string) => {
    setValue("llm_model", version);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={control}
          name="assistantName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Assistant Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="My Assistant"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    const trimmed = (field.value ?? "").trim();
                    if (trimmed !== (field.value ?? "")) field.onChange(trimmed);
                  }}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="useIntegratedAgent"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <FormLabel>Integrated Agent</FormLabel>
                  <FormDescription>
                    Enable the combined question flow experience for this assistant.
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-label="Toggle integrated agent"
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="llm_provider"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Model Provider</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);

                  // When switching providers, set a default model for the new provider
                  const defaultModel = getDefaultModelForProvider(value);
                  if (defaultModel) {
                    setValue("llm_model", defaultModel, { shouldValidate: true });
                  }

                  // Switching into OpenAI Realtime: default the shared "voice"
                  // field to one of its built-in voices if it isn't already one.
                  if (value === "openai-realtime") {
                    const currentVoice = getValues("voice");
                    const isRealtimeVoice = OPENAI_REALTIME_VOICES.some(
                      (v) => v.id === currentVoice,
                    );
                    if (!isRealtimeVoice) {
                      setValue("voice", DEFAULT_OPENAI_REALTIME_VOICE, {
                        shouldValidate: true,
                      });
                    }
                  }
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a model provider" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {providerOptions(field.value).map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />


        <FormField
          control={control}
          name="llm_model"
          render={({ field }) => {
            const availableModels = modelsFor(llm_provider || "");
            const providerName = ALL_MODEL_PROVIDERS.find(p => p.id === llm_provider)?.name || "model";
            
            // Check if the current model value exists in available models
            const modelExists = availableModels.some(model => model.id === field.value);
            
            // Check if we're editing an existing assistant
            const urlParams = new URLSearchParams(window.location.search);
            const assistantId = urlParams.get('assistantId') || window.location.pathname.split('/').pop();
            const isEditingExisting = assistantId && assistantId !== 'new';
            
            // For new assistants, if model doesn't exist in available models, set a default
            if (!isEditingExisting && field.value && !modelExists && availableModels.length > 0) {
              const defaultModel = getDefaultModelForProvider(llm_provider || "");
              if (defaultModel && defaultModel !== field.value) {
                setTimeout(() => {
                  handleLlmModelChange(defaultModel);
                }, 0);
              }
            }
            
            return (
              <FormItem>
                <FormLabel>Model Version</FormLabel>
                {llm_provider && llm_provider !== "custom" && availableModels.length > 0 ? (
                    <Combobox
                      value={field.value || ""}
                      onValueChange={handleLlmModelChange}
                      placeholder={`Select a ${providerName} model`}
                      searchPlaceholder="Search models..."
                      emptyText="No model found."
                      options={availableModels.map((model) => {
                        const details = [
                          model.context && `${(model.context / 1000).toFixed(0)}K tokens`,
                          model.category,
                          model.inputPrice !== undefined &&
                            model.outputPrice !== undefined &&
                            `$${model.inputPrice}/$${model.outputPrice} per M`,
                        ].filter(Boolean);
                        return {
                          value: model.id,
                          label: details.length
                            ? `${model.name} (${details.join(" • ")})`
                            : model.name,
                        };
                      })}
                    />
                ) : (
                  <FormControl>
                    <Input placeholder="e.g., custom-model-name" {...field} />
                  </FormControl>
                )}
                {llm_provider === BEDROCK_PROVIDER_ID && bedrockStatus === "loading" && (
                  <FormDescription>Loading models from AWS Bedrock…</FormDescription>
                )}
                {llm_provider === BEDROCK_PROVIDER_ID && bedrockStatus === "error" && (
                  <FormDescription>
                    Could not load models from AWS Bedrock. Check AWS_BEARER_TOKEN_BEDROCK on the voice server, or type a model id.
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {(llm_provider === "custom" ||
          llm_provider === "livekit-openai" ||
          llm_provider === "openai-realtime" ||
          llm_provider === "custom-realtime") && (
          <>
            <FormField
              control={control}
              name="baseUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://api.example.com/v1"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  {llm_provider === "livekit-openai" && (
                    <FormDescription>
                      OpenAI-compatible endpoint used by the LiveKit OpenAI plugin (e.g. a self-hosted vLLM or LM Studio server).
                    </FormDescription>
                  )}
                  {llm_provider === "openai-realtime" && (
                    <FormDescription>
                      Optional. Realtime API endpoint (e.g. an Azure OpenAI
                      resource or a proxy). Leave blank to use the default
                      OpenAI Realtime endpoint.
                    </FormDescription>
                  )}
                  {llm_provider === "custom-realtime" && (
                    <FormDescription>
                      Your OpenAI Realtime-compatible speech-to-speech
                      endpoint.
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API Key</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Your API key (leave blank if not required)"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        {isGeminiRealtimeModel(llm_provider, llm_model) && (
          <>
            <FormField
              control={control}
              name="realtimeVoice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Realtime Voice</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || DEFAULT_GEMINI_REALTIME_VOICE}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a Gemini Live voice" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {GEMINI_REALTIME_VOICES.map((voice) => (
                        <SelectItem key={voice} value={voice}>
                          {voice}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Prebuilt Gemini Live voice used for the agent's audio output.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="realtimeLanguage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Realtime Language</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || DEFAULT_GEMINI_REALTIME_LANGUAGE}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a language" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {GEMINI_REALTIME_LANGUAGES.map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                          {language.name} ({language.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    BCP-47 language used by Gemini Live for both recognition and speech.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

      </div>

      {/* Show alert if a realtime model is selected */}
      {llm_provider && llm_model && isRealtimeModel(llm_provider, llm_model) && (
        <Alert className="bg-secondary border-border">
          <InfoIcon className="h-4 w-4 text-foreground" />
          <AlertDescription className="text-sm text-foreground">
            {getRealtimeModelMessage(llm_provider, llm_model)}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={control}
          name="temperature"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Temperature: {field.value}</FormLabel>
              <FormControl>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  value={[field.value]}
                  onValueChange={(value) => field.onChange(value[0])}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="maxTokens"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Max Tokens</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  placeholder="1024"
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Service Tier - OpenAI only */}
        {llm_provider === "openai" && (
          <FormField
            control={control}
            name="serviceTier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Tier</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value || "priority"}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service tier" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="priority">Priority</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  OpenAI service tier for API requests. "Priority" (the default)
                  buys lower latency at a higher per-token price; "Default" is
                  standard pricing; "Auto" lets OpenAI choose.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}
